import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import {
  HabitatIntactnessHistoricalChart,
  HIChartData,
} from '@/pages/shared/projects/pages/project/pages/conservation/BU/components/Charts/HabitatIntactnessHistorical';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { useGetTrendLabel } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetTrendLabel';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';

export const GraphTile = () => {
  const { t } = useTranslation();

  const projectAverage = useFact<number>('r1_conservation_threats_i');
  const hiDataRange: [number, number] = useFact<[number, number]>('r1_conservation_habitat_intactness_data_range')
    ?.value ?? [0, 100];
  const hiRangeUpperBound = hiDataRange?.[1];
  const benchmarks = useGetConservationBenchmarks(hiRangeUpperBound);

  const benchmark = getBenchmarkForConservationFact({
    fact: projectAverage,
    benchmarks,
  });

  const habitactIntactnessTimeseries = useFact<{ value: Record<string, number> }>('r1_conservation_threats_i_ts');
  const baselineYear = useFact<{ value: number }>('r1_conservation_habitat_intactness_baseline_year').value;

  const trendLabel = useGetTrendLabel({
    trendChangeFact: 'r1_conservation_habitat_intactness_change_percentage',
  });

  const chartData: HIChartData[] = Object.entries(habitactIntactnessTimeseries.value).map(([yearString, value]) => {
    const hiValue = typeof value === 'number' ? value : 0;

    return {
      hiBar: hiValue,
      hiLine: hiValue,
      date: new Date(yearString),
      benchmark: benchmark ? benchmarks[`${benchmark}`] : null,
    };
  });

  const chartBaseYear = chartData
    ?.sort((a, b) => a.date.getTime() - b.date.getTime())
    ?.at(0)
    ?.date?.getFullYear();

  return (
    <Stack center spacing={6} data-testid='habitat-intactness-graph-tile'>
      <ChartHeading
        heading={t('shared.ncaDetail.habitatIntactness.evolutionBU.chartHeading', {
          year: chartBaseYear ?? baselineYear,
        })}
        className='w-full px-6 sm:px-0'
      />
      <HabitatIntactnessHistoricalChart
        data={chartData}
        benchmark={benchmark}
        trendLabel={trendLabel}
        yAxisUpperBound={hiRangeUpperBound}
      />
    </Stack>
  );
};
